/**
 * # One HTTP read for the compliance workspace
 *
 * Every organization-scoped read in `governance.ts` and `policies.ts` goes
 * through {@link fetchComplianceJson}. It owns three concerns so that the
 * forty-odd atoms above it own none of them:
 *
 * 1. **Where to send the read.** A target with a direct API twin goes to the
 *    domain API with the browser's user token; everything else, and every
 *    fallback, goes to the workspace server proxy with the session cookie
 *    (`domainApi.ts` decides, per request).
 * 2. **Bounded retry.** A brief gateway handover (502/503/504) or a dropped
 *    connection retries at most twice within one 15-second budget. Permission
 *    failures, malformed data and superseded reads never retry.
 * 3. **Honest failure.** Every failure resolves to `{ unavailable: reason }`,
 *    never to an empty record: in a compliance product "you have no
 *    findings" and "we could not reach the service" must never render the
 *    same way.
 *
 * ## Direct-path failure handling
 *
 * | The API says                          | This does                                                              |
 * | ------------------------------------- | ---------------------------------------------------------------------- |
 * | 401 once                              | drop that token (a newer one survives), mint if needed, retry          |
 * | 401 twice in one read                 | the API refuses this server's tokens: hold the proxy for a minute      |
 * | network / CORS error (`TypeError`)    | hold the proxy for a minute; the retry goes through the proxy          |
 * | 502 / 503 / 504                       | retry (same as the proxy path)                                         |
 * | no response within 8 s                | abandon that request and retry (same budget); never wait for the API's |
 * |                                       | five-minute ceiling                                                    |
 * | any other non-2xx                     | `{ unavailable }` with the reason decoded from the body                |
 *
 * ## Cancellation
 *
 * The abort signal stays attached until the body has been read, so a
 * superseded dashboard request stops consuming network and parsing work
 * even after the API has sent headers. Effect interruption drives the
 * signal; a cancelled retry sleep is cancelled too.
 *
 * @module state/complianceFetch
 */
import * as Effect from "effect/Effect";
import * as Schedule from "effect/Schedule";
import * as Schema from "effect/Schema";
import {
  invalidateDomainApiSession,
  markDomainApiUnreachable,
  resolveComplianceRequest,
  type ComplianceTarget,
} from "./domainApi";
import { reasonFromBody } from "./orgProfile";

/** Transport details never leave this boundary: requests contain credentials. */
class ComplianceReadError extends Schema.TaggedErrorClass<ComplianceReadError>()(
  "ComplianceReadError",
  { kind: Schema.Literals(["network", "gateway", "timeout", "token-refused", "protocol"]) },
) {}

const TRANSIENT_STATUSES = new Set([502, 503, 504]);
const RETRY_TIMES = 2;
export const READ_TIMEOUT_MS = 8_000;
/** Includes token resolution, body reads and retry sleeps across both paths. */
export const COMPLIANCE_READ_DEADLINE_MS = 15_000;

const withReadDeadline = <A, E, R>(work: Effect.Effect<A, E, R>, duration: number) =>
  work.pipe(Effect.timeoutOrElse({
    duration,
    orElse: () => Effect.fail(new ComplianceReadError({ kind: "timeout" })),
  }));

/**
 * Fetch and decode one compliance read.
 *
 * @param target - A proxy URL, or a {@link ComplianceTarget} carrying both
 *   the proxy URL and the direct API path.
 * @param decode - Schema decoder for the JSON body. A decode failure is an
 *   `unavailable: "error"`, never a partial record.
 * @returns The decoded value, or `{ unavailable: reason }`. Never fails.
 */
export const fetchComplianceJson = <A, E>(
  target: string | ComplianceTarget,
  decode: (input: unknown) => Effect.Effect<A, E>,
) =>
  Effect.gen(function* () {
    let directRefusals = 0;
    const read = Effect.gen(function* () {
      // Minting is single-flight and shared by all reads. Interrupting this
      // waiter must prevent its fetch without cancelling another page's mint.
      const request = yield* Effect.tryPromise({
        try: () => resolveComplianceRequest(target),
        catch: () => new ComplianceReadError({ kind: "protocol" }),
      });
      return yield* Effect.tryPromise({
        try: async (signal) => {
          let response: Response;
          try {
            response = await fetch(request.url, { ...request.init, method: "GET", signal });
          } catch (error) {
            if (error instanceof TypeError) {
              if (request.direct && !signal.aborted) markDomainApiUnreachable();
              throw new ComplianceReadError({ kind: "network" });
            }
            throw new ComplianceReadError({ kind: "protocol" });
          }
          if (!response.ok) {
            if (request.direct && response.status === 401) {
              await response.body?.cancel();
              directRefusals += 1;
              if (directRefusals >= 2) markDomainApiUnreachable();
              else invalidateDomainApiSession(request.token ?? undefined);
              throw new ComplianceReadError({ kind: "token-refused" });
            }
            if (TRANSIENT_STATUSES.has(response.status)) {
              await response.body?.cancel();
              throw new ComplianceReadError({ kind: "gateway" });
            }
            return { ok: false as const, body: await response.text() };
          }
          return { ok: true as const, json: await response.json() as unknown };
        },
        catch: (error) => error instanceof ComplianceReadError
          ? error
          : new ComplianceReadError({ kind: "protocol" }),
      }).pipe((work) => withReadDeadline(work, READ_TIMEOUT_MS));
    });
    const result = yield* read.pipe(Effect.retry({
      times: RETRY_TIMES,
      while: (error) => error.kind === "network" || error.kind === "gateway"
        || error.kind === "timeout" || error.kind === "token-refused",
      schedule: Schedule.exponential("250 millis"),
    }));
    if (!result.ok) return { unavailable: reasonFromBody(result.body) } as const;
    return yield* decode(result.json);
  }).pipe(
    (work) => withReadDeadline(work, COMPLIANCE_READ_DEADLINE_MS),
    Effect.orElseSucceed(() => ({ unavailable: "error" as const })),
  );
